import { useReducer } from "react";
import { useIntl } from "react-intl";
import { type UseQueryOptions, useQueries } from "@tanstack/react-query";
import type { SearchResponse } from "src/api/SearchResponse";
import type { PlacePair } from "src/domain/TripPlanner/util/getPlacePairs";
import { useSearchParams } from "react-router";
import type { ApiConfig } from "../../api/ApiConfig";
import { useApiConfig } from "../../api/ApiConfigProvider";
import { localeToLanguageCode } from "../conversions/languageCode";
import { ONE_DAY_IN_MILLISECONDS } from "../conversions/time";
import type { SupportedCurrencyCode } from "../currency";
import type { SupportedLanguageCode } from "../language";
import { getCacheKeyForSearch, search } from "../search";
import { useTypedLocation } from "./useTypedLocation";
import useUser from "./useUser";
import { getTransportKeyFromHash } from "./useTripTransportIndex";
import { useIsTripScreen } from "./useIsTripScreen";

export function useGetTripRoutes(places: PlacePair[]) {
  const { currencyCode } = useUser();
  const intl = useIntl();
  const config = useApiConfig();
  const [searchParams] = useSearchParams();
  const searchDate = searchParams.get("oDateTime") ?? "anytime";
  const languageCode = localeToLanguageCode(intl.locale);
  const location = useTypedLocation();
  const activeKey = getTransportKeyFromHash(location.hash);
  const isTripScreen = useIsTripScreen();

  const [activeQueries, setActiveQueries] = useReducer(
    setActiveQueryReducer,
    places.map((placePair) => {
      if (
        `${placePair.origin.canonicalName}_${placePair.destination.canonicalName}` ===
        activeKey
      ) {
        return true;
      }
      return false;
    }),
  );

  const tripPlanQueries = getQueriesForTripPlan(
    activeQueries,
    places,
    currencyCode,
    languageCode,
    searchDate,
    config,
    isTripScreen,
  );

  const allSearchResults = useQueries({ queries: tripPlanQueries });

  return {
    setActiveQueries,
    activeQueries,
    queries: allSearchResults,
    placePairs: places,
    isLoading: allSearchResults.some((result) => result.isLoading),
  };
}

function setActiveQueryReducer(
  activeQueries: boolean[],
  action: { index: number; value: boolean },
) {
  const newActiveQueries = [...activeQueries];
  newActiveQueries[action.index] = action.value;
  return newActiveQueries;
}

function getQueriesForTripPlan(
  activeQueries: boolean[],
  tripPairs: PlacePair[],
  currencyCode: SupportedCurrencyCode,
  languageCode: SupportedLanguageCode,
  searchDate: string,
  config: ApiConfig,
  isTripScreen: boolean,
): UseQueryOptions<SearchResponse>[] {
  let queries: UseQueryOptions<SearchResponse>[] = [];
  tripPairs.forEach((place, index) => {
    const searchKey = getCacheKeyForSearch(
      place.origin.canonicalName,
      place.destination.canonicalName,
      currencyCode,
      languageCode,
      searchDate,
    );
    queries.push({
      queryKey: searchKey,
      queryFn: async () =>
        await search(
          place.origin,
          place.destination,
          currencyCode,
          languageCode,
          config,
        ),
      gcTime: ONE_DAY_IN_MILLISECONDS,
      staleTime: ONE_DAY_IN_MILLISECONDS,
      retryOnMount: false,
      retry: 3,
      refetchOnMount: false,
      enabled: isTripScreen && activeQueries[index],
      notifyOnChangeProps: ["data"],
      throwOnError: true,
    });
  });

  return queries;
}
